import { loadScript, wait } from '@ocx/utils';

import { CookiebotEventName, CookiebotConsentState } from './cookiebot.types';
import { maxInitializationAttempts } from './cookiebot.constants';

export class Cookiebot {
  private static isLoaded: boolean = false;
  private static isLoading: boolean = false;
  private static isError: boolean = false;

  static async init(
    params: {
      sdkUrl: string;
      onLoaded: () => void;
      onError: (error: unknown) => void;
    },
    retryCount: number = 0,
  ): Promise<void> {
    const onLoaded = () => {
      Cookiebot.isError = false;
      Cookiebot.isLoaded = true;
      params.onLoaded();
    };

    const onError = () => {
      Cookiebot.isError = true;
      Cookiebot.isLoaded = false;
      params.onError(new Error('Failed to load Cookiebot script.'));
    };

    // If the script is currently loading, wait and retry initialization until it either loads successfully or reaches the maximum retry attempts.
    if (Cookiebot.isLoading && retryCount <= maxInitializationAttempts) {
      await wait(1000); // Wait for 1 second before checking again
      return Cookiebot.init(params, retryCount + 1); // Try initializing again after waiting
    }

    // If the script is still loading after maximum retry attempts, consider it a failure and call the error callback.
    if (Cookiebot.isLoading && retryCount > maxInitializationAttempts) {
      onError();
      return;
    }

    if (this.isReady()) {
      onLoaded();
      return;
    }

    Cookiebot.isLoading = true;
    await loadScript(params.sdkUrl).catch();
    Cookiebot.isLoading = false;

    if (typeof window.Cookiebot !== 'undefined') {
      onLoaded();
    } else {
      onError();
    }
  }

  static async requestConsent(params: {
    onAccept: (state: CookiebotConsentState) => void;
    onDecline: (state: CookiebotConsentState) => void;
    onError: (error: unknown) => void;
  }) {
    if (!Cookiebot.isReady()) {
      const error = new Error('Cookiebot is not ready. Please initialize it first.');
      params.onError(error);
      return;
    }

    if (window.Cookiebot.consented) {
      params.onAccept(Cookiebot.getConsentState());
      return;
    }

    if (window.Cookiebot.declined) {
      params.onDecline(Cookiebot.getConsentState());
      return;
    }

    const onAccept = () => {
      params.onAccept(Cookiebot.getConsentState());
      window.removeEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
      window.removeEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);
    };

    const onDecline = () => {
      params.onDecline(Cookiebot.getConsentState());
      window.removeEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
      window.removeEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);
    };

    window.addEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
    window.addEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);

    // Cookiebot shows consent popup automatically on load if user has not given or declined consent.
    // However, in some cases, we might want to trigger it manually (e.g., if user wants to change their consent preferences).
    // In such cases, we can call the show method to display the consent popup.
    window.Cookiebot.show();
  }

  static async renewConsent(params: {
    onAccept: (state: CookiebotConsentState) => void;
    onDecline: (state: CookiebotConsentState) => void;
    onError: (error: unknown) => void;
  }) {
    if (!Cookiebot.isReady()) {
      const error = new Error('Cookiebot is not ready. Please initialize it first.');
      params.onError(error);
      return;
    }

    const onAccept = () => {
      params.onAccept(Cookiebot.getConsentState());
      window.removeEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
      window.removeEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);
    };

    const onDecline = () => {
      params.onDecline(Cookiebot.getConsentState());
      window.removeEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
      window.removeEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);
    };

    window.addEventListener('CookiebotOnAccept' satisfies CookiebotEventName, onAccept);
    window.addEventListener('CookiebotOnDecline' satisfies CookiebotEventName, onDecline);

    window.Cookiebot.renew();
  }

  static isReady() {
    return Cookiebot.isLoaded && !Cookiebot.isError;
  }

  static getConsentState(): CookiebotConsentState {
    return {
      consent: window.Cookiebot.consent,
      method: window.Cookiebot.method,
      consented: window.Cookiebot.consented,
      declined: window.Cookiebot.declined,
      hasResponse: window.Cookiebot.hasResponse,
      doNotTrack: window.Cookiebot.doNotTrack,
    };
  }
}
